import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { marketApi } from '../../api/client';
import { useLtpStore } from '../../store/ltpStore';
import { cn } from '../../lib/utils';

function usePrefersReducedMotion() {
  const [prefers, setPrefers] = useState(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
  );
  useEffect(() => {
    const mql = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mql) return;
    const onChange = (e) => setPrefers(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return prefers;
}

function TickerItem({ label, close, open }) {
  const up = close >= open;
  const formatted = close >= 1000 ? close.toFixed(0) : close.toFixed(2);
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 lg:px-4 text-2xs font-mono whitespace-nowrap shrink-0">
      <span className="ticker-label text-txt-label font-semibold tracking-wide">{label}</span>
      <span className="ticker-value text-txt-primary tabular-nums">{formatted}</span>
      <span className={up ? 'text-bull' : 'text-bear'}>{up ? '▲' : '▼'}</span>
      <span className="text-bg-border mx-1 opacity-40">·</span>
    </span>
  );
}

export default function PriceTicker() {
  const [paused, setPaused] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  // Initial fetch — get items list with labels + open prices (one-time)
  const { data } = useQuery({
    queryKey: ['ticker'],
    queryFn: marketApi.getTicker,
    staleTime: 60_000,
    refetchInterval: false,
    retry: false,
  });

  const items = data?.items ?? [];
  const ltpMap = useLtpStore((s) => s.ltp);
  const subscribe = useLtpStore((s) => s.subscribe);
  const unsubscribe = useLtpStore((s) => s.unsubscribe);

  // Subscribe ticker symbols to WS
  const symbols = useMemo(() => items.map((i) => i.symbol).filter(Boolean), [items]);
  const symbolsKey = JSON.stringify(symbols);

  useEffect(() => {
    if (symbols.length === 0) return;
    subscribe(symbols);
    return () => unsubscribe(symbols);
  }, [symbolsKey]);

  // Merge live LTP into ticker items
  const liveItems = useMemo(() => items.map((item) => {
    const live = ltpMap[item.symbol];
    return live != null ? { ...item, close: Number(live) } : item;
  }), [items, ltpMap]);

  if (liveItems.length === 0) return null;

  const shouldScroll = liveItems.length > 3 && !prefersReducedMotion;
  // Render-only: repeat the unique item list enough times to fill one full
  // "lap" wider than any viewport, so the seamless duplicate track (below)
  // never becomes visible alongside the same symbols in one screen. The
  // underlying data (liveItems) stays a single unique list.
  const LAP_MIN_ITEMS = 16;
  const repeatCount = shouldScroll ? Math.max(1, Math.ceil(LAP_MIN_ITEMS / liveItems.length)) : 1;
  const lapItems = shouldScroll
    ? Array.from({ length: repeatCount }, () => liveItems).flat()
    : liveItems;
  const duration = Math.max(20, lapItems.length * 6);

  return (
    <div
      className="ticker-bar h-7 lg:h-8 shrink-0 border-b border-bg-border/60 bg-bg-surface overflow-hidden flex items-center select-none"
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setPaused(false)}
      onTouchCancel={() => setPaused(false)}
    >
      <div
        className={cn('flex items-center', shouldScroll && 'animate-ticker-scroll')}
        style={shouldScroll ? { animationDuration: `${duration}s`, animationPlayState: paused ? 'paused' : 'running' } : undefined}
      >
        {lapItems.map((item, i) => <TickerItem key={i} {...item} />)}
        {shouldScroll && lapItems.map((item, i) => <TickerItem key={`d${i}`} {...item} />)}
      </div>
    </div>
  );
}
